import React, { useRef, useState } from "react";

import {
  Paper,
  Popover,
  Stack,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from "@mui/material";
import { AsyncContentMui } from "components";
import { useDealActions } from "store";
import { IAssetClass } from "store/types";

import { IndexesTableRow } from "./Indexes/IndexesTableRow";
import { SelectedCellContent } from "./Indexes/SelectedCellContent";

const StyledTableHeadCell = styled(TableCell)({
  fontSize: "16px",
  fontWeight: 400,
  color: "#071F35",
  whiteSpace: "nowrap",
  borderBottom: "none",
  maxHeight: "40px"
});

interface IndexesTabProps {
  data: IAssetClass[];
  loading: boolean;
  setData: React.Dispatch<React.SetStateAction<IAssetClass[] | undefined>>;
  expandedRows: number[];
  onExpandRow: (rowId: number) => void;
  privateIndexesOption: { id: number; name: string }[];
}

export const IndexesTab: React.FC<IndexesTabProps> = ({
  data,
  loading,
  setData,
  expandedRows,
  onExpandRow,
  privateIndexesOption
}) => {
  const { updateAssetClassAccountDataAction } = useDealActions();

  const tableContainerRef = useRef<HTMLDivElement>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedCellData, setSelectedCellData] = useState<{
    assetClass: IAssetClass;
    column: "private_index" | "public_index" | "expected_return";
  } | null>(null);

  const updateCellValue = async ({
    anchorEl: element,
    assetClass,
    column
  }: {
    anchorEl: HTMLTableCellElement;
    assetClass: IAssetClass;
    column: "private_index" | "public_index" | "expected_return";
  }) => {
    setAnchorEl(element);
    setSelectedCellData({ assetClass, column });
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSelectedCellData(null);
  };

  const handleSubmit = async (result: { value: string | number | { id: number; name: string } | null }) => {
    if (!selectedCellData) return;

    try {
      const res = await updateAssetClassAccountDataAction({
        id: selectedCellData.assetClass.id,
        [selectedCellData.column]: result.value
      });
      setData((prev) =>
        prev?.map((item) => {
          // sub-asset class update
          if (res.parent_id && item.id === res.parent_id) {
            return {
              ...item,
              sub_asset_class: item.sub_asset_class?.map((subItem) => (subItem.id === res.id ? { ...res } : subItem))
            };
          }
          //parent asset class update
          if (item.id === res.id) {
            return { ...res };
          }
          return item;
        })
      );
    } catch (error) {
      console.log(error);
    }
    handleClose();
  };

  return (
    <Stack sx={{ flexGrow: 1, overflow: "hidden", padding: "16px", backgroundColor: "#FFFFFF", borderRadius: "8px", boxShadow: "0 1px 2px 0 #1212171a", margin: "0 2px 2px 2px" }}>
      <AsyncContentMui spinnerProps={{ size: 120 }} isLoading={loading}>
        <TableContainer
          ref={tableContainerRef}
          component={Paper}
          sx={{
            border: "1px solid #DBE8F5",
            borderRadius: "8px",
            boxShadow: "none",
            position: "relative"
          }}
        >
          <Table size="small" stickyHeader>
            <TableHead sx={{ backgroundColor: "rgba(246, 248, 252, 1)" }}>
              <TableRow sx={{ height: "40px" }}>
                {/* need for expand arrow */}
                <StyledTableHeadCell sx={{ width: 50 }}></StyledTableHeadCell>
                <StyledTableHeadCell sx={{ maxWidth: 120, fontWeight: 500 }}>Asset Classes</StyledTableHeadCell>
                <StyledTableHeadCell sx={{ maxWidth: 120, fontWeight: 500 }}>Sub Asset Classes</StyledTableHeadCell>
                <StyledTableHeadCell sx={{ maxWidth: 120, fontWeight: 500 }}>Private Index</StyledTableHeadCell>
                <StyledTableHeadCell sx={{ maxWidth: 120, fontWeight: 500 }}>Public Index</StyledTableHeadCell>
                <StyledTableHeadCell sx={{ maxWidth: 120, fontWeight: 500 }}>Expected Return</StyledTableHeadCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data?.map((row) => (
                <IndexesTableRow
                  key={row.id}
                  row={row}
                  expandedRows={expandedRows}
                  handleExpandRow={onExpandRow}
                  isChild={false}
                  updateCellValue={updateCellValue}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </AsyncContentMui>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left"
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left"
        }}
      >
        <SelectedCellContent
          selectedCellData={selectedCellData || null}
          onCancel={handleClose}
          onSubmit={handleSubmit}
          privateIndexOptions={privateIndexesOption}
        />
      </Popover>
    </Stack>
  );
};
